import { resolveBackendUrl } from '../api/apiClient';

class WebSocketService {
    constructor() {
        this.ws = null;
        this.listeners = new Set();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.token = null;
    }

    connect(token) {
        if (this.ws?.readyState === WebSocket.OPEN) return;

        this.token = token;

        // The socket server is attached to the same HTTP server as the API, so
        // it always lives on the backend origin — derive it rather than
        // hardcoding a host per environment.
        const isDev = process.env.NODE_ENV === 'development';
        const devHostname = window.location.hostname || 'localhost';
        const wsUrl = isDev
            ? `ws://${devHostname}:5001?token=${token}&app=captions`
            : `${String(resolveBackendUrl() || '').replace(/^http/, 'ws')}?token=${token}&app=captions`;

        try {
            this.ws = new WebSocket(wsUrl);

            this.ws.onopen = () => {
                console.log('WebSocket connected');
                this.reconnectAttempts = 0;
            };

            this.ws.onmessage = event => {
                try {
                    const data = JSON.parse(event.data);
                    this.listeners.forEach(listener => listener(data));
                } catch (err) {
                    console.error('WebSocket parse error:', err);
                }
            };

            this.ws.onclose = event => {
                console.log('WebSocket closed:', event.code, event.reason);

                if (event.code === 1000 || event.code === 4001 || event.code === 4003) {
                    return;
                }

                if (this.reconnectAttempts < this.maxReconnectAttempts && this.token) {
                    this.reconnectAttempts++;
                    const delay = Math.min(2000 * this.reconnectAttempts, 30000);
                    console.log(`WebSocket reconnecting in ${delay}ms`);
                    setTimeout(() => this.connect(this.token), delay);
                }
            };

            this.ws.onerror = err => {
                console.error('WebSocket error:', err);
            };
        } catch (err) {
            console.error('WebSocket connection failed:', err);
        }
    }

    disconnect() {
        this.token = null;
        this.reconnectAttempts = this.maxReconnectAttempts;
        if (this.ws) {
            this.ws.close(1000, 'Intentional disconnect');
            this.ws = null;
        }
    }

    send(message) {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
            return true;
        }
        return false;
    }

    addListener(callback) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    sendCaption(text, timestamp, sourceLanguage = null, sessionId = null) {
        return this.send({
            type: 'caption',
            text,
            timestamp,
            id: `caption-${timestamp}`,
            sourceLanguage,
            sessionId,
        });
    }

    isConnected() {
        return this.ws?.readyState === WebSocket.OPEN;
    }
}

export const websocketService = new WebSocketService();
